import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';

export interface WishlistSyncStatus {
  appId: number;
  gameName: string | null;
  minDate: string | null;
  syncedThrough: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
}

export interface SteamStatus {
  wishlists?: WishlistSyncStatus[];
  key: { hint: string; updatedAt: string } | null;
  publisherKey?: { hint: string; updatedAt: string } | null;
  sync: {
    lastSuccessAt: string | null;
    lastRunAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    consecutiveFailures: number;
    pendingDates: number;
    datesSynced: number;
  } | null;
}

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-GB') : 'never');

export function useSteamStatus() {
  return useQuery({ queryKey: ['steam-status'], queryFn: () => api<SteamStatus>('GET', '/api/steam/status'), refetchInterval: 30_000 });
}

export function SteamPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const status = useSteamStatus();
  const [key, setKey] = useState('');
  const refresh = () => Promise.all(['steam-status', 'steam-reconciliation'].map((k) => client.invalidateQueries({ queryKey: [k] })));
  const saveKey = useMutation({ mutationFn: () => api('PUT', '/api/steam/key', { key }), onSuccess: async () => { setKey(''); await refresh(); } });
  const [publisherKey, setPublisherKey] = useState('');
  const savePublisherKey = useMutation({
    mutationFn: () => api<{ apps: number }>('PUT', '/api/steam/publisher-key', { key: publisherKey }),
    onSuccess: async () => { setPublisherKey(''); await refresh(); },
  });
  const removePublisherKey = useMutation({ mutationFn: () => api('DELETE', '/api/steam/publisher-key'), onSuccess: refresh });
  const sync = useMutation({ mutationFn: () => api<{ queued: boolean }>('POST', '/api/steam/sync'), onSuccess: refresh });
  const s = status.data?.sync;

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Steam sync</h1>
      <dl className="grid max-w-2xl grid-cols-[12rem_1fr] gap-y-1 text-sm">
        <dt className="text-slate-500">Financial API key</dt>
        <dd>{status.data?.key ? `${status.data.key.hint} (set ${when(status.data.key.updatedAt)})` : 'not configured'}</dd>
        <dt className="text-slate-500">Publisher key (app list)</dt>
        <dd>{status.data?.publisherKey ? `${status.data.publisherKey.hint} (set ${when(status.data.publisherKey.updatedAt)})` : 'not configured: the game catalog uses sales data and the store only'}</dd>
        <dt className="text-slate-500">Last successful sync</dt>
        <dd>{when(s?.lastSuccessAt)}</dd>
        <dt className="text-slate-500">Last run</dt>
        <dd>{when(s?.lastRunAt)}</dd>
        <dt className="text-slate-500">Dates waiting</dt>
        <dd>{s?.pendingDates ?? 0}</dd>
        <dt className="text-slate-500">Dates synced in total</dt>
        <dd>{s?.datesSynced ?? 0}</dd>
        {s?.lastError && (
          <>
            <dt className="text-red-700 dark:text-red-400">Last error</dt>
            <dd className="text-red-700 dark:text-red-400">{s.lastError} ({when(s.lastErrorAt)}, {s.consecutiveFailures} in a row)</dd>
          </>
        )}
      </dl>
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3">
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveKey.mutate(); }} className="flex items-end gap-2">
            <div className="w-96">
              <Input label={status.data?.key ? 'Replace key' : 'Publisher Web API key (Financial permission)'} type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value.trim())} />
            </div>
            <Button type="submit" disabled={saveKey.isPending || key.length === 0}>Test and save</Button>
          </form>
          <Button onClick={() => sync.mutate()} disabled={sync.isPending || !status.data?.key}>Sync now</Button>
        </div>
      )}
      {isAdmin && (
        <div className="space-y-2">
          <h2 className="font-semibold">Publisher key for the game catalog</h2>
          <p className="text-sm text-slate-500">
            A publisher Web API key of a normal key group with all apps assigned (not the Financial group: apps cannot be assigned to it). The game
            catalog reads the partner app list with it, which also names apps without sales or store page. Tested before it is stored.
          </p>
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); savePublisherKey.mutate(); }} className="flex flex-wrap items-end gap-2">
            <div className="w-96">
              <Input label={status.data?.publisherKey ? 'Replace publisher key' : 'Publisher Web API key'} type="password" autoComplete="off" value={publisherKey} onChange={(e) => setPublisherKey(e.target.value.trim())} />
            </div>
            <Button type="submit" disabled={savePublisherKey.isPending || publisherKey.length === 0}>Test and save</Button>
            {status.data?.publisherKey && <Button type="button" onClick={() => removePublisherKey.mutate()} disabled={removePublisherKey.isPending}>Remove</Button>}
          </form>
          {savePublisherKey.data && <p className="text-sm text-slate-500">Stored; the app list has {savePublisherKey.data.apps} app(s){savePublisherKey.data.apps === 0 ? ': assign the apps to the key group' : ''}.</p>}
          {savePublisherKey.isError && <ErrorText>{savePublisherKey.error instanceof ApiError ? savePublisherKey.error.message : 'Could not save the key'}</ErrorText>}
        </div>
      )}
      <div>
        <h2 className="mb-2 font-semibold">Wishlist sync</h2>
        <p className="mb-2 text-sm text-slate-500">
          Daily at 01:30 UTC per game with a Steam app id (GetAppWishlistReporting); a new app is read back to Steam&apos;s first wishlist date.
        </p>
        {(status.data?.wishlists ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No wishlist data synced yet.</p>
        ) : (
          <table className="text-sm">
            <thead className="text-left text-slate-500">
              <tr><th className="pr-6 font-normal">Game</th><th className="pr-6 font-normal">From</th><th className="pr-6 font-normal">Complete through</th><th className="pr-6 font-normal">Last success</th><th className="font-normal">Error</th></tr>
            </thead>
            <tbody>
              {status.data!.wishlists!.map((w) => (
                <tr key={w.appId}>
                  <td className="pr-6">{w.gameName ?? `App ${w.appId}`}</td>
                  <td className="pr-6">{w.minDate ?? '-'}</td>
                  <td className="pr-6">{w.syncedThrough ?? '-'}</td>
                  <td className="pr-6">{when(w.lastSuccessAt)}</td>
                  <td className="text-red-700 dark:text-red-400">{w.lastError ? `${w.lastError} (${w.consecutiveFailures} in a row)` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {saveKey.isError && <ErrorText>{saveKey.error instanceof ApiError ? saveKey.error.message : 'Could not save the key'}</ErrorText>}
      {sync.data && !sync.data.queued && <p className="text-sm text-slate-500">A sync is already queued or running.</p>}
      <p className="text-sm text-slate-500">The monthly reconciliation against the Steam reports is on the <Link to="/reports" className="underline">Reports</Link> page.</p>
    </section>
  );
}
